import {
  cloneElement,
  createElement,
  CSSProperties,
  ReactElement,
  ReactNode,
} from "react";
import cx from "classnames";
import styles from "./FlexLayout.module.css";

export type Align = "start" | "center" | "end" | "stretch" | "baseline";
export type Direction =
  | "vertical"
  | "horizontal"
  | "vertical-reverse"
  | "horizontal-reverse";
export type Justify = "start" | "center" | "end" | "all" | "around" | "evenly";
export type Wrap = "nowrap" | "wrap" | "wrap-reverse";
type Element =
  | "article"
  | "aside"
  | "div"
  | "footer"
  | "header"
  | "main"
  | "nav"
  | "section"
  | "li"
  | "ul"
  | "form"
  | ReactElement<any>;

export interface FlexLayoutProps {
  align?: Align;
  children: ReactNode | ReactNode[];
  className?: string;
  direction: Direction;
  element?: Element;
  justify?: Justify;
  style?: CSSProperties;
  wrap?: Wrap;
  onClick?: () => void;
}

export default function FlexLayout({
  align = "start",
  children,
  className,
  direction,
  element = "div",
  justify = "start",
  style,
  wrap = "nowrap",
  onClick,
}: FlexLayoutProps) {
  const elementProps = {
    className: flexLayout(
      {
        align,
        direction,
        justify,
        wrap,
      },
      className
    ),
    style,
    children,
    onClick,
  };
  return typeof element === "string"
    ? createElement(element, elementProps)
    : cloneElement(element, elementProps);
}

flexLayout.defaults = {
  align: "start" as Align,
  justify: "start" as Justify,
  wrap: "nowrap" as Wrap,
};

export function flexLayout(
  layout: {
    align?: Align;
    direction?: Direction;
    justify?: Justify;
    wrap?: Wrap;
  },
  className?: string
): string {
  const { align, direction, justify, wrap } = {
    ...flexLayout.defaults,
    ...layout,
  };
  return cx(className, {
    [styles.root]: true,
    [styles.justifyStart]: justify === "start",
    [styles.justifyCenter]: justify === "center",
    [styles.justifyEnd]: justify === "end",
    [styles.justifyAll]: justify === "all",
    [styles.justifyAround]: justify === "around",
    [styles.justifyEvenly]: justify === "evenly",
    [styles.directionVertical]: direction === "vertical",
    [styles.directionHorizontal]: direction === "horizontal",
    [styles.directionVerticalReverse]: direction === "vertical-reverse",
    [styles.directionHorizontalReverse]: direction === "horizontal-reverse",
    [styles.alignStart]: align === "start",
    [styles.alignCenter]: align === "center",
    [styles.alignEnd]: align === "end",
    [styles.alignStretch]: align === "stretch",
    [styles.alignBaseline]: align === "baseline",
    [styles.wrap]: wrap === "wrap",
    [styles.wrapReverse]: wrap === "wrap-reverse",
  });
}

type Layout = {
  direction: Direction;
  align?: Align;
  justify?: Justify;
};

// Common layout patterns
export const layouts = {
  vertical: { direction: "vertical", align: "start" } as Layout,
  horizontal: { direction: "horizontal", align: "start" } as Layout,
  horizontalCenter: {
    direction: "horizontal",
    align: "center",
  } as Layout,
  horizontalStretch: {
    direction: "horizontal",
    align: "stretch",
  } as Layout,
  horizontalCenterAll: {
    direction: "horizontal",
    align: "center",
    justify: "center",
  } as Layout,
  verticalCenter: {
    direction: "vertical",
    align: "center",
  } as Layout,
  verticalCenterAll: {
    direction: "vertical",
    align: "center",
    justify: "center",
  } as Layout,
  verticalStretch: {
    direction: "vertical",
    align: "stretch",
  } as Layout,
};
